import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Directive, Inject, Input, PLATFORM_ID, TemplateRef, ViewContainerRef } from '@angular/core';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Structural directive that conditionally removes components from the DOM based on sale state.
 * When a component has the 'hide-on-sale' style and a sale is active, the component is removed
 * from the DOM entirely (not just hidden with CSS).
 *
 * The directive checks the sale state synchronously during initialization (both SSR and browser)
 * to prevent layout shifts. Components are rendered immediately if no sale is active, or not
 * rendered at all if a sale is active.
 *
 * Usage:
 * <div *hideOnSale="componentStyles">Content</div>
 *
 * @example
 * // In template:
 * <div *hideOnSale="['hide-on-sale', 'other-style']">
 *   This will be removed from DOM when sale is active
 * </div>
 */
@Directive({
  selector: '[hideOnSale]',
  standalone: true,
})
export class GraphicBannerHideOnSaleDirective implements OnInit, OnDestroy {
  /**
   * Array of style strings from Craft CMS.
   * If includes 'hide-on-sale' (case-insensitive), component will be hidden during sales.
   */
  @Input() hideOnSale: string[] = [];
  private destroy$ = new Subject<void>();
  private hasView = false;
  private isBrowser: boolean;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private countdownService: CountdownService,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    const shouldHideOnSale = this.hideOnSale?.some((style) => style?.toLowerCase() === 'hide-on-sale');

    if (!shouldHideOnSale) {
      // No hide-on-sale style, always show the component
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
      return;
    }

    // Get the current sale state synchronously (works in both SSR and browser)
    const isSaleActive = this.countdownService.getGraphicBannerSaleValue().value;

    // Render component based on initial sale state (no layout shift)
    if (!isSaleActive) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }

    // Only subscribe to sale value changes in browser environment
    if (this.isBrowser) {
      this.countdownService
        .getGraphicBannerSaleValue()
        .pipe(takeUntil(this.destroy$))
        .subscribe((saleActive) => {
          if (saleActive && this.hasView) {
            // Sale is active: remove component from DOM
            this.viewContainer.clear();
            this.hasView = false;
          } else if (!saleActive && !this.hasView) {
            // Sale is inactive: add component to DOM
            this.viewContainer.createEmbeddedView(this.templateRef);
            this.hasView = true;
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
